import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Email,
  CheckCircle,
  Error as ErrorIcon,
  ArrowForward,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const steps = ['Connect Email Account', 'Authorize Access', 'Complete'];

function ConnectEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState(null);

  const successParam = searchParams.get('success');
  const errorParam = searchParams.get('error');

  useEffect(() => {
    // Check if coming from OAuth callback
    if (successParam === 'true') {
      setSuccess(true);
      setActiveStep(2);
      // Refresh user data to get updated email connection status
      if (refreshUser) {
        refreshUser();
      }
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/clinician');
      }, 3000);
    } else if (errorParam) {
      setError(decodeURIComponent(errorParam).replace(/_/g, ' '));
      setActiveStep(0);
    }

    // Check current connection status
    checkConnectionStatus();
  }, [successParam, errorParam, refreshUser, navigate]);

  const checkConnectionStatus = async () => {
    try {
      const response = await api.get('/nylas/connect/status');
      if (response.data.success) {
        setConnectionStatus(response.data);
        if (response.data.connected) {
          setActiveStep(2);
          setSuccess(true);
        }
      }
    } catch (err) {
      console.error('Error checking connection status:', err);
    }
  };

  const handleConnectEmail = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);
    setActiveStep(1);

    try {
      const response = await api.get('/nylas/connect/initiate');
      
      if (response.data.success) {
        if (response.data.alreadyConnected) {
          setSuccess(true);
          setActiveStep(2);
          if (refreshUser) {
            refreshUser();
          }
        } else if (response.data.authUrl) {
          // Redirect to Nylas OAuth
          window.location.href = response.data.authUrl;
        } else {
          throw new Error('No authorization URL received');
        }
      } else {
        throw new Error(response.data.error || 'Failed to initiate email connection');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to connect email. Please try again.');
      setActiveStep(0);
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    setActiveStep(0);
    handleConnectEmail();
  };

  const handleContinue = () => {
    navigate('/clinician');
  };

  if (success && activeStep === 2) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Email Connected Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your facility email account has been connected. You can now send emails from the application.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            sx={{ mt: 2 }}
          >
            Continue to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Email sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Connect Your Email Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            To send emails from your facility's email address, you need to authorize access to your mailbox.
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {connectionStatus && connectionStatus.connected && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Email is already connected. You can send emails now.
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Why Connect Your Email?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              When you send emails from the application, they will be sent from your facility's email address
              ({connectionStatus?.facility?.email || user?.facility?.email || 'your facility email'}).
              This ensures recipients see the email coming from your official facility address.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Supported Providers:</strong> Gmail, Microsoft Outlook/Office365, and IMAP-based email providers.
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          {error && (
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRetry}
              disabled={loading}
            >
              Retry
            </Button>
          )}
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
            onClick={handleConnectEmail}
            disabled={loading || (connectionStatus && connectionStatus.connected)}
          >
            {loading ? 'Connecting...' : connectionStatus?.connected ? 'Already Connected' : 'Connect Email'}
          </Button>
        </Box>

        {activeStep === 1 && !error && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Redirecting to email provider for authorization...
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default ConnectEmail;
